/**
 * Implementation milestones for one onboarded client.
 *
 * A milestone carries an estimate in days. Starting it sets the deadline
 * (start + planned days); missing that makes it overdue. Work can be put on
 * hold, and the side that caused the hold matters: a client-side hold pushes
 * the deadline out by the days lost, a hold on our own side does not. Every
 * transition takes a note, and the whole log is readable per row.
 *
 * Lifted out of OnboardedDetail.js: the tab needs its own state for the
 * action dialog, the per-row log and the share link, and the detail page was
 * already carrying eight tabs' worth.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Select } from "antd";
import {
  AlertCircle, CheckCircle2, ChevronDown, ChevronRight, Clock, Copy, Eye,
  EyeOff, Layers, Link2, Lock, Pause, Pencil, Play, Plus, RotateCcw, Save,
  Square, StickyNote, Trash2, X, Check, RefreshCw,
} from "lucide-react";

import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../common/ToastMsg";
import { Button } from "../ui/button";
import { Input, Textarea } from "../ui/input";
import {
  ONBOARDED_MILESTONES_URL, ONBOARDED_MILESTONE_URL,
  ONBOARDED_MILESTONE_ACTION_URL, ONBOARDED_MILESTONE_EVENTS_URL,
  ONBOARDED_MILESTONE_EVENT_URL, ONBOARDED_STATUS_LINK_URL,
  ONBOARDED_PLAN_URL, ONBOARDED_SAVE_TEMPLATE_URL,
} from "../../constants/api";
import PlanBanner from "./PlanBanner";
import ApplyTemplateModal from "./ApplyTemplateModal";

const card = "bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700";

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const today = () => new Date().toISOString().slice(0, 10);

const MS_COLOR = {
  "Pending":     "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400",
  "In Progress": "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  "On Hold":     "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  "Done":        "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  "Stopped":     "bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-300",
  "Delayed":     "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
};

// Label, icon and whether the dialog must collect a side, per action.
const ACTION_META = {
  start:    { label: "Start",    icon: Play,     needsSide: false, title: "Start work" },
  hold:     { label: "Hold",     icon: Pause,    needsSide: true,  title: "Put on hold" },
  resume:   { label: "Resume",   icon: Play,     needsSide: false, title: "Resume work" },
  complete: { label: "Complete", icon: Check,    needsSide: false, title: "Mark complete" },
  stop:     { label: "Stop",     icon: Square,   needsSide: true,  title: "Stop work" },
  reopen:   { label: "Reopen",   icon: RotateCcw, needsSide: false, title: "Reopen" },
};

const EVENT_LABEL = {
  created: "Created", started: "Started", held: "Put on hold", resumed: "Resumed",
  completed: "Completed", stopped: "Stopped", reopened: "Reopened",
  note: "Note", rescheduled: "Schedule changed",
};

const emptyForm = {
  milestone_name: "", planned_days: "", started_date: "", due_date: "",
  owner_id: "", notes: "",
};

export default function MilestonesPanel({ clientId, userOptions = [], onChanged }) {
  const { fetch, create, update, remove } = useApiMethods();

  const [rows, setRows]       = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm]       = useState(emptyForm);
  const [saving, setSaving]   = useState(false);

  const [editId, setEditId]   = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);

  // Which rows have their log open, and the log itself once fetched.
  const [openLog, setOpenLog] = useState({});
  const [logs, setLogs]       = useState({});

  // The action dialog: { milestone, action }.
  const [pending, setPending] = useState(null);
  const [actionForm, setActionForm] = useState({
    side: "client", note: "", is_client_visible: false, on_date: today(),
  });

  const [noteFor, setNoteFor] = useState(null);
  const [noteForm, setNoteForm] = useState({ note: "", is_client_visible: false });

  const [link, setLink]       = useState(null);
  const [linkBusy, setLinkBusy] = useState(false);

  // The plan and its approval state. Served alongside the milestones so the
  // banner and the row buttons can never disagree about whether work is gated.
  const [plan, setPlan]             = useState(null);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [showTemplate, setShowTemplate]     = useState(false);
  const [saveTplName, setSaveTplName]       = useState(null);   // null = dialog closed

  const load = useCallback(() => {
    if (!clientId) return;
    fetch(ONBOARDED_MILESTONES_URL(clientId), {}, setLoading).then(res => {
      if (res?.status === "success") {
        setRows(res.data || []);
        setSummary(res.summary || null);
        setPlan(res.plan || null);
      }
    });
    // Change requests live on the plan endpoint, not the milestone list, so
    // the approver's queue is one call whichever screen opened it.
    fetch(ONBOARDED_PLAN_URL(clientId)).then(res => {
      if (res?.status === "success") {
        setPlan(res.data || null);
        setPendingChanges(res.pending_changes || []);
      }
    });
  }, [clientId]);   // eslint-disable-line react-hooks/exhaustive-deps

  const loadLink = useCallback(() => {
    if (!clientId) return;
    fetch(ONBOARDED_STATUS_LINK_URL(clientId)).then(res => {
      if (res?.status === "success") setLink(res.data);
    });
  }, [clientId]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); loadLink(); }, [load, loadLink]);

  const refresh = () => { load(); loadLink(); onChanged?.(); };

  const saveAsTemplate = async () => {
    const name = (saveTplName || "").trim();
    if (!name) return ToastMsg("Give the template a name", "error");
    setSaving(true);
    const res = await create(ONBOARDED_SAVE_TEMPLATE_URL(clientId), { name });
    setSaving(false);
    if (res?.status === "success") {
      ToastMsg(res.message || "Saved as template", "success");
      setSaveTplName(null);
    } else ToastMsg(res?.message || "Failed to save the template", "error");
  };

  // ── the log, fetched per row on first expand ─────────────────────────────
  const toggleLog = (id) => {
    const isOpen = !!openLog[id];
    setOpenLog(p => ({ ...p, [id]: !isOpen }));
    if (!isOpen && !logs[id]) {
      fetch(ONBOARDED_MILESTONE_EVENTS_URL(id)).then(res => {
        if (res?.status === "success") setLogs(p => ({ ...p, [id]: res.data || [] }));
      });
    }
  };

  const reloadLog = (id) =>
    fetch(ONBOARDED_MILESTONE_EVENTS_URL(id)).then(res => {
      if (res?.status === "success") setLogs(p => ({ ...p, [id]: res.data || [] }));
    });

  // ── create / edit / delete ───────────────────────────────────────────────
  const add = async () => {
    if (!form.milestone_name.trim()) return ToastMsg("Milestone name is required", "error");
    setSaving(true);
    const res = await create(ONBOARDED_MILESTONES_URL(clientId), {
      ...form,
      planned_days: form.planned_days === "" ? null : Number(form.planned_days),
    });
    setSaving(false);
    if (res?.status === "success") {
      ToastMsg("Milestone added", "success");
      setForm(emptyForm);
      setShowAdd(false);
      refresh();
    } else ToastMsg(res?.message || "Failed to add", "error");
  };

  const saveEdit = async (id) => {
    const res = await update(ONBOARDED_MILESTONE_URL(id), {
      milestone_name: editForm.milestone_name,
      planned_days: editForm.planned_days === "" ? null : Number(editForm.planned_days),
      started_date: editForm.started_date || null,
      due_date: editForm.due_date || null,
      owner_id: editForm.owner_id || null,
      notes: editForm.notes || "",
    });
    if (res?.status === "success") {
      // The server decides whether a date edit applied or was parked for
      // approval, so its message is the one worth showing.
      ToastMsg(res.message || "Milestone updated",
               res.requires_approval ? "info" : "success");
      setEditId(null);
      if (openLog[id]) reloadLog(id);
      refresh();
    } else ToastMsg(res?.message || "Failed to update", "error");
  };

  const del = async (id) => {
    if (!window.confirm("Remove this milestone? Its log goes with it.")) return;
    const res = await remove(ONBOARDED_MILESTONE_URL(id));
    if (res?.status === "success") { ToastMsg("Milestone removed", "success"); refresh(); }
    else ToastMsg(res?.message || "Failed to remove", "error");
  };

  // ── the workflow ─────────────────────────────────────────────────────────
  const openAction = (m, action) => {
    setPending({ milestone: m, action });
    setActionForm({
      // A hold defaults to the client side: that is the common case, and the
      // one where getting the side wrong silently costs the team its deadline.
      side: action === "hold" ? "client" : "",
      note: "",
      is_client_visible: action !== "hold" ? false : true,
      on_date: today(),
    });
  };

  const submitAction = async () => {
    if (!pending) return;
    const { milestone, action } = pending;
    if (ACTION_META[action]?.needsSide && !actionForm.side)
      return ToastMsg("Choose who this is waiting on", "error");

    setSaving(true);
    const res = await create(ONBOARDED_MILESTONE_ACTION_URL(milestone.id), {
      action,
      side: actionForm.side || undefined,
      note: actionForm.note,
      is_client_visible: actionForm.is_client_visible,
      on_date: actionForm.on_date || undefined,
    });
    setSaving(false);
    if (res?.status === "success") {
      ToastMsg(res.message || "Updated", "success");
      setPending(null);
      if (openLog[milestone.id]) reloadLog(milestone.id);
      refresh();
    } else ToastMsg(res?.message || "Failed", "error");
  };

  // ── notes ────────────────────────────────────────────────────────────────
  const addNote = async () => {
    if (!noteForm.note.trim()) return ToastMsg("Note cannot be empty", "error");
    setSaving(true);
    const res = await create(ONBOARDED_MILESTONE_EVENTS_URL(noteFor.id), noteForm);
    setSaving(false);
    if (res?.status === "success") {
      ToastMsg("Note added", "success");
      setNoteFor(null);
      setNoteForm({ note: "", is_client_visible: false });
      setOpenLog(p => ({ ...p, [noteFor.id]: true }));
      reloadLog(noteFor.id);
    } else ToastMsg(res?.message || "Failed to add note", "error");
  };

  const toggleVisible = async (msId, ev) => {
    const res = await update(ONBOARDED_MILESTONE_EVENT_URL(ev.id), {
      is_client_visible: !ev.is_client_visible,
    });
    if (res?.status === "success") reloadLog(msId);
    else ToastMsg(res?.message || "Failed", "error");
  };

  const delNote = async (msId, ev) => {
    const res = await remove(ONBOARDED_MILESTONE_EVENT_URL(ev.id));
    if (res?.status === "success") reloadLog(msId);
    else ToastMsg(res?.message || "Failed", "error");
  };

  // ── the client status link ───────────────────────────────────────────────
  const createLink = async (rotate = false) => {
    setLinkBusy(true);
    const res = await create(ONBOARDED_STATUS_LINK_URL(clientId), { rotate });
    setLinkBusy(false);
    if (res?.status === "success") {
      setLink(res.data);
      ToastMsg(res.message || "Link ready", "success");
    } else ToastMsg(res?.message || "Failed", "error");
  };

  const revokeLink = async () => {
    if (!window.confirm("Revoke the client's link? The URL stops working immediately.")) return;
    setLinkBusy(true);
    const res = await remove(ONBOARDED_STATUS_LINK_URL(clientId));
    setLinkBusy(false);
    if (res?.status === "success") { setLink(res.data); ToastMsg("Link revoked", "success"); }
    else ToastMsg(res?.message || "Failed", "error");
  };

  const copyLink = () => {
    if (!link?.url) return;
    // navigator.clipboard needs a secure context; the prompt fallback still
    // lets someone copy the URL over plain http on the LAN.
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(link.url)
        .then(() => ToastMsg("Link copied", "success"))
        .catch(() => window.prompt("Copy this link", link.url));
    } else window.prompt("Copy this link", link.url);
  };

  const s = summary || { total: rows.length, done: 0, percent: 0, overdue: 0, on_hold: 0 };
  const ownerOptions = useMemo(() => userOptions, [userOptions]);

  return (
    <div className="space-y-4">
      <PlanBanner clientId={clientId} plan={plan} pendingChanges={pendingChanges}
        onChanged={refresh} />

      {/* ── header: progress, share link, add ──────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-2 w-32 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
            <div className="h-full bg-blue-500 rounded-full" style={{ width: `${s.percent}%` }} />
          </div>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {s.done}/{s.total} done ({s.percent}%)
          </span>
          {s.overdue > 0 && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
              {s.overdue} overdue
            </span>
          )}
          {s.on_hold > 0 && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
              {s.on_hold} on hold
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={load} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          {rows.length > 0 && (
            <Button size="sm" variant="outline" onClick={() => setSaveTplName("")}>
              <Save className="h-4 w-4 mr-1" /> Save as template
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => setShowTemplate(true)}>
            <Layers className="h-4 w-4 mr-1" /> Use template
          </Button>
          <Button size="sm" onClick={() => setShowAdd(v => !v)}>
            <Plus className="h-4 w-4 mr-1" /> Add Milestone
          </Button>
        </div>
      </div>

      {/* days lost, when there are any — the reason the side is recorded */}
      {(s.client_hold_days > 0 || s.internal_hold_days > 0) && (
        <div className="flex flex-wrap gap-4 text-xs text-gray-500 dark:text-gray-400">
          <span>
            Days lost waiting on the client:{" "}
            <b className="text-gray-700 dark:text-gray-200">{s.client_hold_days || 0}</b>
            <span className="text-gray-400"> (deadlines moved out)</span>
          </span>
          <span>
            Days lost on our side:{" "}
            <b className="text-gray-700 dark:text-gray-200">{s.internal_hold_days || 0}</b>
            <span className="text-gray-400"> (deadlines unchanged)</span>
          </span>
        </div>
      )}

      {/* ── the client's status link ────────────────────────────────────── */}
      <div className={`${card} p-3`}>
        <div className="flex flex-wrap items-center gap-3">
          <Link2 className="h-4 w-4 text-gray-400 shrink-0" />
          {link?.enabled && link?.url ? (
            <>
              <code className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 break-all flex-1 min-w-[12rem]">
                {link.url}
              </code>
              <Button size="sm" variant="outline" onClick={copyLink}>
                <Copy className="h-3.5 w-3.5 mr-1" /> Copy
              </Button>
              <Button size="sm" variant="outline" onClick={() => createLink(true)} disabled={linkBusy}>
                Reissue
              </Button>
              <Button size="sm" variant="outline" className="text-red-500" onClick={revokeLink} disabled={linkBusy}>
                Revoke
              </Button>
            </>
          ) : (
            <>
              <p className="text-xs text-gray-500 dark:text-gray-400 flex-1">
                Share a read-only status page with the client — no login. They see each
                task, its dates, who a hold is waiting on, and any note you publish.
              </p>
              <Button size="sm" onClick={() => createLink(false)} disabled={linkBusy}>
                {link?.token ? "Re-enable link" : "Create link"}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* ── add form ───────────────────────────────────────────────────── */}
      {showAdd && (
        <div className={`${card} p-4 space-y-3`}>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="md:col-span-2">
              <Label>Milestone name *</Label>
              <Input placeholder="e.g. Server Setup" value={form.milestone_name}
                onChange={e => setForm(p => ({ ...p, milestone_name: e.target.value }))} />
            </div>
            <div>
              <Label>Takes (days)</Label>
              <Input type="number" min="0" placeholder="2" value={form.planned_days}
                onChange={e => setForm(p => ({ ...p, planned_days: e.target.value }))} />
            </div>
            <div>
              <Label>Start date</Label>
              <Input type="date" value={form.started_date}
                onChange={e => setForm(p => ({ ...p, started_date: e.target.value }))} />
            </div>
            <div>
              <Label>Owner</Label>
              <Select showSearch allowClear className="w-full" placeholder="Assign to"
                value={form.owner_id || undefined} optionFilterProp="label"
                options={ownerOptions}
                onChange={v => setForm(p => ({ ...p, owner_id: v || "" }))} />
            </div>
          </div>
          <p className="text-[11px] text-gray-400">
            Leave the start date empty to add it to the queue — the deadline is set when
            you press Start, as start + {form.planned_days || "n"} days.
          </p>
          <div>
            <Label>Notes</Label>
            <Input placeholder="Optional" value={form.notes}
              onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={add} disabled={saving}>Add</Button>
            <Button size="sm" variant="outline" onClick={() => { setShowAdd(false); setForm(emptyForm); }}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* ── the list ───────────────────────────────────────────────────── */}
      {rows.length === 0 ? (
        <div className={`${card} p-8 flex flex-col items-center gap-3 text-gray-400 dark:text-gray-500`}>
          <CheckCircle2 className="h-8 w-8 opacity-30" />
          <p className="text-sm">No milestones yet</p>
        </div>
      ) : (
        <div className={`${card} overflow-hidden`}>
          {rows.map((m, idx) => (
            <div key={m.id} className={idx > 0 ? "border-t border-gray-200 dark:border-gray-700" : ""}>
              {editId === m.id ? (
                <div className="p-3 grid grid-cols-1 md:grid-cols-6 gap-2 items-end bg-gray-50 dark:bg-gray-700/30">
                  <div className="md:col-span-2">
                    <Label>Name</Label>
                    <Input value={editForm.milestone_name}
                      onChange={e => setEditForm(p => ({ ...p, milestone_name: e.target.value }))} />
                  </div>
                  <div>
                    <Label>Days</Label>
                    <Input type="number" min="0" value={editForm.planned_days}
                      onChange={e => setEditForm(p => ({ ...p, planned_days: e.target.value }))} />
                  </div>
                  <div>
                    <Label>Started</Label>
                    <Input type="date" value={editForm.started_date || ""}
                      onChange={e => setEditForm(p => ({ ...p, started_date: e.target.value }))} />
                  </div>
                  <div>
                    <Label>Due</Label>
                    <Input type="date" value={editForm.due_date || ""}
                      onChange={e => setEditForm(p => ({ ...p, due_date: e.target.value }))} />
                  </div>
                  <div className="flex gap-1">
                    <Button size="icon" className="h-8 w-8" onClick={() => saveEdit(m.id)}>
                      <Check className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setEditId(null)}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <p className="md:col-span-6 text-[11px] text-gray-400">
                    Changing the estimate or start date re-derives the due date, keeping any
                    extension a client hold already earned. Set Due directly to override it.
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <button onClick={() => toggleLog(m.id)}
                      className="shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                      title="Show the log">
                      {openLog[m.id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>

                    <div className="w-5 h-5 shrink-0">
                      {m.status === "Done" ? <CheckCircle2 className="h-5 w-5 text-green-500" />
                        : m.status === "On Hold" ? <Pause className="h-5 w-5 text-amber-500" />
                        : m.status === "Stopped" ? <Square className="h-5 w-5 text-gray-400" />
                        : m.is_overdue ? <AlertCircle className="h-5 w-5 text-red-500" />
                        : <Clock className="h-5 w-5 text-gray-400 dark:text-gray-500" />}
                    </div>

                    <div className="flex-1 min-w-[10rem]">
                      <p className={`text-sm font-medium ${m.status === "Done" ? "line-through text-gray-400" : "text-gray-900 dark:text-white"}`}>
                        {m.milestone_name}
                        {m.planned_days != null && (
                          <span className="ml-2 text-xs font-normal text-gray-400">
                            {m.planned_days}d
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {m.started_date ? `Started ${fmtDate(m.started_date)}` : "Not started"}
                        {m.effective_due_date && ` · due ${fmtDate(m.effective_due_date)}`}
                        {m.completed_date && ` · done ${fmtDate(m.completed_date)}`}
                      </p>
                      {m.status === "On Hold" && (
                        <p className="text-xs text-amber-600 dark:text-amber-400">
                          On hold since {fmtDate(m.hold_since)} — {m.hold_side_label}
                          {m.open_hold_days > 0 && ` (${m.open_hold_days}d)`}
                          {m.hold_side === "client" && " · deadline moving with it"}
                        </p>
                      )}
                      {m.notes && <p className="text-xs text-gray-400 truncate">{m.notes}</p>}
                    </div>

                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${MS_COLOR[m.status] || MS_COLOR.Pending}`}>
                      {m.status}
                    </span>
                    {m.pending_change && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium shrink-0
                                       bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                        title={`Requested ${m.pending_change.requested_due_date} — awaiting approval`}>
                        change pending
                      </span>
                    )}
                    {m.is_overdue && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium shrink-0 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                        {m.days_overdue}d overdue
                      </span>
                    )}
                    <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0 w-24 truncate">
                      {m.owner_name || "—"}
                    </span>

                    {/* transitions the row can actually take */}
                    <div className="flex flex-wrap gap-1 shrink-0">
                      {m.is_gated && (
                        <span className="inline-flex items-center gap-1 rounded-md bg-gray-100
                                         px-2 py-1 text-[11px] text-gray-500 dark:bg-gray-700
                                         dark:text-gray-400"
                          title={plan?.block_reason || "The plan is not approved yet"}>
                          <Lock className="h-3 w-3" /> Locked
                        </span>
                      )}
                      {(m.available_actions || []).map(a => {
                        const meta = ACTION_META[a];
                        if (!meta) return null;
                        const Icon = meta.icon;
                        return (
                          <Button key={a} size="sm" variant="outline" className="h-7 px-2 text-xs"
                            onClick={() => openAction(m, a)}>
                            <Icon className="h-3 w-3 mr-1" />{meta.label}
                          </Button>
                        );
                      })}
                      <Button size="icon" variant="ghost" className="h-7 w-7" title="Add note"
                        onClick={() => { setNoteFor(m); setNoteForm({ note: "", is_client_visible: false }); }}>
                        <StickyNote className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" title="Edit plan"
                        onClick={() => {
                          setEditId(m.id);
                          setEditForm({
                            milestone_name: m.milestone_name,
                            planned_days: m.planned_days ?? "",
                            started_date: m.started_date || "",
                            due_date: m.due_date || "",
                            owner_id: m.owner_id || "",
                            notes: m.notes || "",
                          });
                        }}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500 hover:text-red-600"
                        title="Remove" onClick={() => del(m.id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>

                  {/* ── the log ─────────────────────────────────────────── */}
                  {openLog[m.id] && (
                    <div className="px-4 pb-3 pl-12 bg-gray-50/60 dark:bg-gray-700/20">
                      {!logs[m.id] ? (
                        <p className="text-xs text-gray-400 py-2">Loading…</p>
                      ) : logs[m.id].length === 0 ? (
                        <p className="text-xs text-gray-400 py-2">Nothing logged yet.</p>
                      ) : (
                        <ul className="space-y-2 py-2">
                          {logs[m.id].map(ev => (
                            <li key={ev.id} className="flex items-start gap-2 text-xs">
                              <span className="text-gray-400 shrink-0 w-20">{fmtDate(ev.event_date)}</span>
                              <span className="font-medium text-gray-700 dark:text-gray-200 shrink-0">
                                {EVENT_LABEL[ev.event_type] || ev.event_type}
                              </span>
                              {ev.side_label && (
                                <span className={`px-1.5 rounded shrink-0 ${ev.side === "client"
                                  ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                                  : "bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-300"}`}>
                                  {ev.side_label}
                                </span>
                              )}
                              {ev.days_impact > 0 && (
                                <span className="px-1.5 rounded shrink-0 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                                  +{ev.days_impact}d to due date
                                </span>
                              )}
                              <span className="flex-1 text-gray-600 dark:text-gray-300 break-words">
                                {ev.note}
                              </span>
                              <span className="text-gray-400 shrink-0">{ev.actor_name || "—"}</span>
                              {(ev.note || "").trim() && (
                                <button onClick={() => toggleVisible(m.id, ev)}
                                  className="shrink-0 text-gray-400 hover:text-blue-500"
                                  title={ev.is_client_visible
                                    ? "Visible to the client — click to hide"
                                    : "Internal only — click to publish to the client"}>
                                  {ev.is_client_visible
                                    ? <Eye className="h-3.5 w-3.5 text-blue-500" />
                                    : <EyeOff className="h-3.5 w-3.5" />}
                                </button>
                              )}
                              {ev.event_type === "note" && (
                                <button onClick={() => delNote(m.id, ev)}
                                  className="shrink-0 text-gray-400 hover:text-red-500" title="Remove note">
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── action dialog ──────────────────────────────────────────────── */}
      {pending && (
        <Modal title={`${ACTION_META[pending.action]?.title || pending.action} — ${pending.milestone.milestone_name}`}
          onClose={() => setPending(null)}>
          {ACTION_META[pending.action]?.needsSide && (
            <div className="mb-3">
              <Label>{pending.action === "hold" ? "Waiting on *" : "Reason side"}</Label>
              <div className="flex gap-2 mt-1">
                {[["client", "Client side"], ["internal", "Our side"]].map(([val, label]) => (
                  <button key={val} onClick={() => setActionForm(p => ({ ...p, side: val }))}
                    className={`px-3 py-1.5 rounded-md text-xs border ${actionForm.side === val
                      ? "border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                      : "border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300"}`}>
                    {label}
                  </button>
                ))}
              </div>
              {pending.action === "hold" && (
                <p className="text-[11px] text-gray-400 mt-1">
                  {actionForm.side === "client"
                    ? "The deadline moves out by however long this hold lasts."
                    : "The deadline does not move — these days count against us."}
                </p>
              )}
            </div>
          )}

          <div className="mb-3">
            <Label>Date</Label>
            <Input type="date" value={actionForm.on_date}
              onChange={e => setActionForm(p => ({ ...p, on_date: e.target.value }))} />
          </div>

          <div className="mb-3">
            <Label>Note</Label>
            <Textarea rows={3} placeholder="What happened, and why"
              value={actionForm.note}
              onChange={e => setActionForm(p => ({ ...p, note: e.target.value }))} />
          </div>

          <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 mb-4">
            <input type="checkbox" checked={actionForm.is_client_visible}
              onChange={e => setActionForm(p => ({ ...p, is_client_visible: e.target.checked }))} />
            Show this note on the client's status page
          </label>

          <div className="flex gap-2">
            <Button size="sm" onClick={submitAction} disabled={saving}>
              {ACTION_META[pending.action]?.label || "Apply"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setPending(null)}>Cancel</Button>
          </div>
        </Modal>
      )}

      {showTemplate && (
        <ApplyTemplateModal
          clientId={clientId}
          planStartDate={plan?.plan_start_date}
          onClose={() => setShowTemplate(false)}
          onApplied={refresh}
        />
      )}

      {saveTplName !== null && (
        <Modal title="Save these milestones as a template" onClose={() => setSaveTplName(null)}>
          <p className="mb-3 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
            The task names and day estimates are copied. Owners and dates are not —
            who did it here says nothing about the next client.
          </p>
          <Input placeholder="e.g. Standard clinic onboarding" value={saveTplName}
            onChange={e => setSaveTplName(e.target.value)} />
          <div className="mt-4 flex gap-2">
            <Button size="sm" onClick={saveAsTemplate} disabled={saving}>Save template</Button>
            <Button size="sm" variant="outline" onClick={() => setSaveTplName(null)}>Cancel</Button>
          </div>
        </Modal>
      )}

      {/* ── note dialog ────────────────────────────────────────────────── */}
      {noteFor && (
        <Modal title={`Add note — ${noteFor.milestone_name}`} onClose={() => setNoteFor(null)}>
          <Textarea rows={4} placeholder="Note" value={noteForm.note}
            onChange={e => setNoteForm(p => ({ ...p, note: e.target.value }))} />
          <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 my-3">
            <input type="checkbox" checked={noteForm.is_client_visible}
              onChange={e => setNoteForm(p => ({ ...p, is_client_visible: e.target.checked }))} />
            Show this note on the client's status page
          </label>
          <div className="flex gap-2">
            <Button size="sm" onClick={addNote} disabled={saving}>Add note</Button>
            <Button size="sm" variant="outline" onClick={() => setNoteFor(null)}>Cancel</Button>
          </div>
        </Modal>
      )}
    </div>
  );
}

const Label = ({ children }) => (
  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{children}</label>
);

const Modal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    onClick={onClose}>
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 w-full max-w-md p-4 max-h-[90vh] overflow-y-auto"
      onClick={e => e.stopPropagation()}>
      <div className="flex items-start justify-between mb-3 gap-3">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
          <X className="h-4 w-4" />
        </button>
      </div>
      {children}
    </div>
  </div>
);
